 <template>
  <transition name="fade" mode="out-in">
    <AgGridVue
      :key="isDark ? 'dark' : 'light'"
      style="flex: 1;"
      :grid-options="options"
      :rowData="rowData"
      :overlayNoRowsTemplate="noRowsTemplate"
      @grid-ready="onGridReady"
      @selection-changed="onSelectionChanged"
      @first-data-rendered="onFirstDataRendered"
    />
  </transition>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';
import { AgGridVue } from 'ag-grid-vue3'
import { GridApi, GridOptions } from 'ag-grid-community';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';

import UserActions from './cells/UserActions.vue';

import { MemberDetails } from '@/types/member.js';

import { useAllOrgnisationRoles } from '@/queries/organisation.query';
import { useDeleteMember, useMembersDetails } from '@/queries/members.query.js';
import appRouter from '@/router/adminRouter.js';
import { useToast } from 'vue-toastification';

const toast = useToast();
const isDark = useDark();
const { t } = useI18n();
const appStore = useAppStore();

let gridApi: GridApi;

const noRowsTemplate = ref(`<div>${t('app.tables.userTable.noRowTemplate')}</div>`);

const { data: organisationRoles, isPending: isOrganisationRolesPending } = useAllOrgnisationRoles();
const { data: members, isPending: isMembersPending } = useMembersDetails();
const { mutate: deleteMember } = useDeleteMember();

const props = withDefaults(defineProps<{
  search: {
    value: string,
    searchable: string[],
  }
}>(), {
  search: () => {
    return {
      value: "",
      searchable: [
        "name",
      ],
    }
  }
});

const emit = defineEmits<{
  (emit: "edit-user", value: MemberDetails): void;
}>();

const options = computed(() => {
  return {
    theme: isDark.value ? themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#1F1F1F",
      fontFamily: 'Inter, sans-serif',
    }).withPart(colorSchemeDark) : themeQuartz.withParams({
      accentColor: '#F2CB36',
      oddRowBackgroundColor: "#FAFAFA",
      fontFamily: 'Inter, sans-serif',
    }),
    suppressCellFocus: true,
    rowSelection: {
      mode: 'multiRow',
      enableClickSelection: true,
      enableSelectionWithoutKeys: true,
    },
    defaultColDef: {
      sortable: true,
      flex: 1,
      resizable: false,
      unSortIcon: true
    },
    columnDefs: [
      { field: 'name', headerName: t('other.table.columns.user'), flex: 1 },
      { field: 'email', headerName: t('other.table.columns.email'), flex: 1 },
      { field: 'role', headerName: t('other.table.columns.role'), flex: 0 },
      {
        headerName: 'Actions',
        field: 'actions',
        sortable: false,
        filter: false,
        flex: 0,
        cellRenderer: UserActions,
        cellRendererParams: {
          onEdit: (row) => editUser(row),
          onDelete: (row) => onDelete(row),
        }
      },
    ]
  } as GridOptions;
});

const rowData = computed(() => {
  if (isMembersPending.value || isOrganisationRolesPending.value) return [];
  return members?.value?.map((v, index) => {
    const appRole = organisationRoles.value?.find((role) =>  role.id === v.roles_ids[0]);
    return {
      id: index,
      name: `${v.account.personal_information.first_name} ${v.account.personal_information.last_name}`,
      email: v.account.personal_information.email,
      role: appRole?.name,
    }
  })
  .filter((v) => {
    const searchValue = props.search.value.toLocaleLowerCase();
    if (v.email.toLocaleLowerCase().includes(searchValue)) return true;
    if (v.name.toLocaleLowerCase().includes(searchValue)) return true;
    return false;
  });
});

function editUser(userRow: any) {
  if (userRow?.id == undefined || userRow?.id === null) return;
  emit('edit-user', members.value[userRow?.id]);
}

function onDelete(userRow: any) {
  if (userRow?.id == undefined || userRow?.id === null) return;
  const member = members.value[userRow?.id];
  deleteMember({
    organisationId: appStore.currentOrganisationId,
    memberId: member.member.id,
  }, {
    onSuccess: () => {
      toast.success(t('app.toasts.success'));
    },
    onError: () => { 
      toast.success(t('other.error.ERROR_UNKNOWN'));
    }
  });
}

function onGridReady(params) {
  gridApi = params.api
}

function onSelectionChanged() {
  // const selectedRows = gridApi.getSelectedRows();
  // console.log('Selected:', selectedRows);
}

function onFirstDataRendered() {
  gridApi.autoSizeColumns(['actions']);
}
</script>

<style lang="scss"></style>